'use client';

import { useEffect, useLayoutEffect, useMemo } from 'react';
import Cart from '@/components/organisms/cart';
import useCart from '@/lib/hooks/useCart';
import useAccount from '@/lib/hooks/useAccount';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { mapCoCoAddress } from '@/utils/mappers/map-address';
import { TasticProps } from '../types';
import { Props } from './types';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useCartConfig } from '@/providers/cart-config/CartConfigContext';

const CartTastic = ({ }: TasticProps<Props>) => {
  const router = useRouter();
  const { account } = useAccount();
  const locale = useLocale();
  const { isCartEnabled, isLoading } = useCartConfig();

  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();

  const { cart, addItem, updateItem, removeItem, requestQuote, updateCart, updateItemQuantity, removeItemFromCart, getCart, removeAllItemsFromCart, addCoupon, removeCoupon } = useCart(
    "",
    locale === 'au' ? "plumbershub-au" : "plumbershub-nz",
  );

  const defaultShippingAddress = useMemo(() => {
    return (
      (selectedBusinessUnit?.addresses ?? []).find((address) => !!address.isDefaultShipping) ??
      selectedBusinessUnit?.addresses[0]
    );
  }, [selectedBusinessUnit]);


  useEffect(() => {
    getCart();
  }, []);

  useEffect(() => {
    if (defaultShippingAddress) {
      updateCart({ shipping: mapCoCoAddress(defaultShippingAddress) });
    }
  }, [defaultShippingAddress, updateCart]);

  useEffect(() => {
    if (!isLoading && !isCartEnabled) {
      router.push(`/${locale}/`);
    }
  }, [isCartEnabled, isLoading, router, locale]);


  return (
    <Cart
      {...cart}
      account={{ email: account?.email ?? '' }}
      paymentMethods={[]}
      onAdd={async (sku, count) => {
        addItem([{ sku, count }]);
      }}
      onUpdateQuantity={async (variantSku: string, quantity: number, variantKey: any) => {
        await updateItemQuantity(variantSku, quantity, variantKey);
      }}
      onRemove={removeItemFromCart}
      clearCart={removeAllItemsFromCart}
      onAddCoupon={addCoupon}
      onRemoveCoupon={removeCoupon}
      onRequestQuote={async ({ buyerComment }) => {
        const quote = await requestQuote({ buyerComment: buyerComment ?? '' });

        return quote.quoteRequestId ? { id: quote.quoteRequestId } : {};
      }}
    />
  );
};

export default CartTastic;
